<template>
    <div class="home fill-height">
        <Navigation/>
        <v-main class="fill-height" style="height: calc(100% - 120px)">
            <v-overlay absolute :value="loading" color="white" opacity="0.5">
                <v-progress-circular indeterminate size="64" color="primary"></v-progress-circular>
            </v-overlay>
            <v-container fluid class="pa-0" fill-height>
                <v-row no-gutters class="fill-height">
                    <v-col>
                        <l-map
                            ref="map"
                            :zoom="zoom"
                            :center="center"
                            style="width: 100%; height: 100%; z-index: 0;"
                        >
                            <l-tile-layer
                                :url="tileUrl"
                                :attribution="attribution"
                            />
                            <l-marker
                                v-for="(marker, i) in markers"
                                :key="i"
                                :lat-lng="marker.pos"
                            >
                                <l-icon
                                    :icon-url="marker.icon"
                                    :icon-size="[36, 48]"
                                    :icon-anchor="[18, 48]"
                                    :popup-anchor="[0, -48]"
                                />
                                <l-popup>
                                    <div class="workshop-popup">
                                        <h3 class="popup-address">{{ marker.address }}</h3>
                                        <hr>
                                        <div v-for="workshop in marker.workshops" :key="workshop.id" class="popup-workshop">
                                            <h2>
                                                <router-link
                                                    :to="{ name: 'Workshop', params: { id: workshop.id } }"
                                                    class="workshop-link"
                                                >
                                                    {{ workshop.name }}
                                                </router-link>
                                            </h2>
                                            <p>
                                                Professionnel: <strong>{{ workshop.user.firstname }}</strong>
                                                <template v-if="workshop.secondary_pro">
                                                    et {{ workshop.secondary_pro.firstname }}
                                                </template>
                                                <br>
                                                <template v-if="workshop.next_onsite_meeting_start">
                                                    Prochaine séance présentielle:
                                                    <strong>
                                                        le {{ $moment(workshop.next_onsite_meeting_start).format('dddd DD MMMM YYYY') }}
                                                        à {{ $moment(workshop.next_onsite_meeting_start).format('HH:mm') }}
                                                    </strong>
                                                </template>
                                                <template v-else>
                                                    Aucune séance présentielle à venir
                                                </template>
                                            </p>
                                            <div class="text-center">
                                                <router-link
                                                    :to="{ name: 'Workshop', params: { id: workshop.id } }"
                                                    class="v-btn v-btn--depressed theme--light v-size--default primary"
                                                >
                                                    <b>M'inscrire</b>
                                                </router-link>
                                            </div>
                                        </div>
                                    </div>
                                </l-popup>
                            </l-marker>
                        </l-map>
                    </v-col>
                </v-row>
                <v-dialog v-model="showHelp" max-width="1200px">
                    <v-card color="primary">
                        <v-card-title class="justify-end">
                            <v-btn icon @click="showHelp = false"><v-icon>close</v-icon></v-btn>
                        </v-card-title>
                        <v-card-text class="text-center">
                            <div class="headline text-center font-weight-bold">
                                Ateliers VISIO accessibles quelle que soit votre localisation sur la France entière
                            </div>
                            <div class="text-center mt-3">
                                <v-btn @click="showHelp = false">Ok</v-btn>
                            </div>
                        </v-card-text>
                    </v-card>
                </v-dialog>
            </v-container>
        </v-main>
    </div>
</template>

<script>
import '@/plugins/leaflet'
import { mapActions } from 'vuex'

export default {
    name: 'WorkshopsMap',
    data() {
        return {
            workshops: [],
            markers: [],
            // Configuration de la carte centrée sur la France
            zoom: 6,
            center: [46.8202448, 2.5169702],
            // Tuiles OpenStreetMap
            tileUrl: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
            loading: false,
            showHelp: true,
            search: {
                ...(this.$route.query.search ? JSON.parse(this.$route.query.search) : { type: 'onsite' }),
                active: true
            },
        }
    },
    computed: {
        // Icônes SVG personnalisées aux couleurs OnCogite
        markerIcons() {
            return {
                // Orange OnCogite pour présentiel
                onsite: this.createMarkerIcon('#f7921d'),
                // Rouge pour visio
                remote: this.createMarkerIcon('#e53935'),
                // Violet pour online
                online: this.createMarkerIcon('#8e24aa')
            }
        }
    },
    mounted() {
        this.setThemeAja(false)
        this.refreshPagination()
    },
    methods: {
        ...mapActions(['setThemeAja']),
        refreshPagination() {
            const queries = {
                search: Object.keys(this.search).length > 0 ? JSON.stringify(this.search) : null,
                with: ['user', 'partner'],
            }

            this.loading = true
            this.workshops = []
            this.$store.dispatch('workshopsRequest', queries)
                .then(result => {
                    this.workshops = result.data
                    this.setMarkers(this.workshops)
                    this.loading = false
                })
                .catch((err) => {
                    this.loading = false
                    if (err.response && err.response.status != 403)
                        this.$store.dispatch('setErrors', err.response.data)
                })
        },
        setMarkers(workshops) {
            this.markers = []

            workshops.forEach((workshop) => {
                // Utilise les coordonnées pré-calculées du partner
                if (workshop.partner && workshop.partner.latitude && workshop.partner.longitude) {
                    const address = this.buildAddress(workshop.partner)

                    // Vérifie si un marker existe déjà à cette position (pour grouper les ateliers)
                    const existingMarker = this.markers.find(m =>
                        m.pos[0] === workshop.partner.latitude &&
                        m.pos[1] === workshop.partner.longitude
                    )

                    if (existingMarker) {
                        existingMarker.workshops.push(workshop)
                    } else {
                        this.markers.push({
                            workshops: [workshop],
                            address: address,
                            pos: [workshop.partner.latitude, workshop.partner.longitude],
                            icon: this.markerIcons[workshop.type] || this.markerIcons.onsite
                        })
                    }
                }
            })
        },
        buildAddress(partner) {
            return [partner.street, partner.zip, partner.city]
                .filter(Boolean)
                .join(' ')
        },
        createMarkerIcon(color) {
            const svg = `
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 36 48" width="36" height="48">
                    <defs>
                        <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
                            <feDropShadow dx="0" dy="2" stdDeviation="2" flood-opacity="0.3"/>
                        </filter>
                    </defs>
                    <path fill="${color}" filter="url(#shadow)" d="M18 0C8.06 0 0 8.06 0 18c0 12.6 16.2 28.4 16.9 29.1.6.6 1.6.6 2.2 0C19.8 46.4 36 30.6 36 18 36 8.06 27.94 0 18 0z"/>
                    <circle fill="white" cx="18" cy="18" r="8"/>
                </svg>
            `
            return 'data:image/svg+xml;base64,' + btoa(svg)
        }
    },
}
</script>

<style scoped>
.workshop-popup {
    padding: 10px;
    min-width: 250px;
}

.popup-address {
    margin-bottom: 10px;
    color: #333;
}

.popup-workshop {
    margin-top: 10px;
}

.popup-workshop h2 {
    font-size: 1.1rem;
    margin-bottom: 5px;
}

.workshop-link {
    text-decoration: none;
    color: var(--v-primary-base, #1976d2);
}

.workshop-link:hover {
    text-decoration: underline;
}

.popup-workshop p {
    color: #333;
    margin-bottom: 10px;
}
</style>
